import React from 'react';
import { Button } from '../Button';
import { Input } from '../Input';
import { Ripple } from 'primereact/ripple';
import { PaginatorTemplateOptions } from './types';
import { Dropdown as DSDropdown } from '../Dropdown';
import { Dropdown } from 'primereact/dropdown';
import { OnChangeParams } from '../Dropdown/types';
import { borderColor, marginSize } from '../../Utilities/utils/Theming';

const layoutSimple = 'FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink';
const layoutWithPageLinks =
	'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport';
const layout = 'RowsPerPageDropdown CurrentPageReport PrevPageLink NextPageLink';
const layoutToolbar = 'FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink';

const dropdownOptions = [
	{ label: 5, value: 5 },
	{ label: 10, value: 10 },
	{ label: 20, value: 20 },
	{ label: 50, value: 50 },
];

const FirstPageLink = (options: any) => {
	return (
		<Button
			aria-label="First Page"
			iconAfter="first_page"
			className="first-page-link"
			theme="minimal"
			onClick={options.onClick}
			disabled={options.disabled}
		></Button>
	);
};

const PrevPageLink = (options: any) => {
	return (
		<Button
			aria-label="Prev"
			iconAfter="navigate_before"
			theme="minimal"
			className="prev-page-link"
			onClick={options.onClick}
			disabled={options.disabled}
		></Button>
	);
};

const NextPageLink = (options: any) => {
	return (
		<Button
			aria-label="Next"
			iconAfter="navigate_next"
			theme="minimal"
			className="next-page-link"
			onClick={options.onClick}
			disabled={options.disabled}
		></Button>
	);
};

const LastPageLink = (options: any) => {
	return (
		<Button
			aria-label="Last Page"
			iconAfter="last_page"
			theme="minimal"
			className="last-page-link"
			onClick={options.onClick}
			disabled={options.disabled}
		></Button>
	);
};

const FirstPageLinkWithBorder = (options: any) => {
	return (
		<Button
			aria-label="First Page"
			iconAfter="first_page"
			theme="tertiary"
			className="first-page-link-border"
			onClick={options.onClick}
			disabled={options.disabled}
		></Button>
	);
};

const PrevPageLinkWithBorder = (options: any) => {
	return (
		<Button
			aria-label="Prev"
			iconAfter="navigate_before"
			theme="tertiary"
			className="prev-page-link-border"
			onClick={options.onClick}
			disabled={options.disabled}
		></Button>
	);
};

const NextPageLinkWithBorder = (options: any) => {
	return (
		<Button
			aria-label="Next"
			iconAfter="navigate_next"
			theme="tertiary"
			className="next-page-link-border"
			onClick={options.onClick}
			disabled={options.disabled}
		></Button>
	);
};

const LastPageLinkWithBorder = (options: any) => {
	return (
		<Button
			aria-label="Last Page"
			iconAfter="last_page"
			theme="tertiary"
			className="last-page-link-border"
			onClick={options.onClick}
			disabled={options.disabled}
		></Button>
	);
};

const RowsPerPageDropdownDropdownTemplate = (options: any) => {
	return (
		<span>
			<Dropdown
				value={options.value}
				options={dropdownOptions}
				onChange={options.onChange}
				appendTo={document.body}
			/>
		</span>
	);
};

const RowsPerPageDropdownDropdownTemplateWithLabel = (options: any) => {
	return (
		<>
			<span style={{ userSelect: 'none', marginRight: '12px' }}>Items per page </span>
			<span>
				<Dropdown
					value={options.value}
					options={dropdownOptions}
					onChange={options.onChange}
					appendTo={document.body}
				/>
			</span>
		</>
	);
};

const CurrentPageReportWithSimpleInput = (props: any) => {
	return (
		<span>
			<Input
				style={{ width: '60px', height: '30px' }}
				value={props.page}
				onKeyDown={(event: React.KeyboardEvent) => onPageInputKeyDown(event, props)}
				onChange={(event: React.ChangeEvent<HTMLInputElement>) => onPageInputChange(event, props)}
			/>
		</span>
	);
};

const CurrentPageReportWithInputAndLabel = (props: any) => {
	const totalPages = Math.ceil(props.totalRecords / props.rows);

	return (
		<div style={{ display: 'flex' }}>
			<Input
				style={{ width: '60px', height: '30px' }}
				value={props.page}
				onKeyDown={(event: React.KeyboardEvent) => onPageInputKeyDown(event, props)}
				onChange={(event: React.ChangeEvent<HTMLInputElement>) => onPageInputChange(event, props)}
			/>
			<span
				style={{
					whiteSpace: 'nowrap',
					marginLeft: '12px',
					marginRight: '12px',
					marginBottom: 'auto',
					marginTop: 'auto',
				}}
			>
				of {totalPages}
			</span>
		</div>
	);
};

const CurrentPageReportWithInputAndGoToLabel = (props: any) => {
	return (
		<div style={{ display: 'flex' }}>
			<span
				style={{
					whiteSpace: 'nowrap',
					marginLeft: '12px',
					marginRight: '12px',
					marginBottom: 'auto',
					marginTop: 'auto',
				}}
			>
				Go to
			</span>
			<Input
				style={{ width: '60px', height: '30px' }}
				value={props.page}
				onKeyDown={(event: React.KeyboardEvent) => onPageInputKeyDown(event, props)}
				onChange={(event: React.ChangeEvent<HTMLInputElement>) => onPageInputChange(event, props)}
			/>
		</div>
	);
};

type TItem = {
	label: number,
	id: number,
};

const CurrentPageReportWithSearch = (props: any) => {
	let items: TItem[] = [];
	const totalPages = Math.ceil(props.totalRecords / props.rows);

	for (let i = 1; i < totalPages + 1; i++) {
		items.push({
			label: i,
			id: i,
		});
	}

	return (
		<span>
			<DSDropdown
				id="epo-dropdown-97586"
				name="epo-dropdown-97586"
				ControlContainerStyle={{
					minWidth: '56px',
					maxWidth: '70px',
					borderRadius: 0,
					...borderColor('var(--epods--color-border)'),
				}}
				clearable={false}
				preset="combo"
				placeholder=" "
				value={props.page}
				onChange={(e: OnChangeParams) => onDropdownPageChange(e, props)}
				options={items}
				className="paginator-combo-select"
				maxDropdownHeight="200px"
				IconsContainerStyle={{ paddingRight: '0' }}
				noResultsMsg=" "
				aria-label="paginator-dropdown"
				DropdownContainerStyle={{ maxWidth: '100px' }}
				overrides={{
					Root: {
						style: ({ $isFocused }: any) => {
							const isFocused = $isFocused ? { zIndex: 1 } : { zIndex: 0 };
							return {
								...isFocused,
								position: 'relative',
							};
						},
					},
				}}
			/>
		</span>
	);
};

const onPageInputKeyDown = (event: any, props: any) => {
	if (event.key === 'Enter') {
		const page = props.page;
		const first = page ? props.rows * (page - 1) : 0;
		props.setCustomFirst(first);
	}
};

const onPageInputChange = (event: any, props: any) => {
	props.setCurrentPage(event.target.value);
};

const onDropdownPageChange = (event: any, props: any) => {
	const page = event.value[0]?.id;
	const first = page ? props.rows * (page - 1) : 0;

	props.setCustomFirst(first);
	props.setCurrentPage(event.value);
};

export const Label: PaginatorTemplateOptions = {
	layout: layoutSimple,
	FirstPageLink: FirstPageLink,
	PrevPageLink: PrevPageLink,
	NextPageLink: NextPageLink,
	LastPageLink: LastPageLink,
};

export const LabelBorders: PaginatorTemplateOptions = {
	layout: layoutSimple,
	FirstPageLink: FirstPageLinkWithBorder,
	PrevPageLink: PrevPageLinkWithBorder,
	NextPageLink: NextPageLinkWithBorder,
	LastPageLink: LastPageLinkWithBorder,
};

export const GoTo: PaginatorTemplateOptions = {
	layout: layoutSimple,
	FirstPageLink: FirstPageLink,
	PrevPageLink: PrevPageLink,
	NextPageLink: NextPageLink,
	LastPageLink: LastPageLink,
};

export const GoToBorder: PaginatorTemplateOptions = {
	layout: layoutSimple,
	FirstPageLink: FirstPageLinkWithBorder,
	PrevPageLink: PrevPageLinkWithBorder,
	NextPageLink: NextPageLinkWithBorder,
	LastPageLink: LastPageLinkWithBorder,
};

export const GoToLabel: PaginatorTemplateOptions = {
	layout: layoutSimple,
	FirstPageLink: FirstPageLink,
	PrevPageLink: PrevPageLink,
	NextPageLink: NextPageLink,
	LastPageLink: LastPageLink,
};

export const GoToLabelBorder: PaginatorTemplateOptions = {
	layout: layoutSimple,
	FirstPageLink: FirstPageLinkWithBorder,
	PrevPageLink: PrevPageLinkWithBorder,
	NextPageLink: NextPageLinkWithBorder,
	LastPageLink: LastPageLinkWithBorder,
};

export const ItemsLabel: PaginatorTemplateOptions = {
	layout: layout,
	RowsPerPageDropdown: RowsPerPageDropdownDropdownTemplateWithLabel,
	CurrentPageReport: (options: any) => {
		return (
			<span style={{ userSelect: 'none', margin: '0 var(--epods--paginator--page-link-margin)', textAlign: 'center' }}>
				{options.first} - {options.last} of {options.totalRecords}
			</span>
		);
	},
	PrevPageLink: PrevPageLink,
	NextPageLink: NextPageLink,
};

export const ItemsLabelBorder: PaginatorTemplateOptions = {
	layout: layout,
	RowsPerPageDropdown: RowsPerPageDropdownDropdownTemplateWithLabel,
	CurrentPageReport: (options: any) => {
		return (
			<span style={{ userSelect: 'none', margin: '0 var(--epods--paginator--page-link-margin)', textAlign: 'center' }}>
				{options.first} - {options.last} of {options.totalRecords}
			</span>
		);
	},
	PrevPageLink: PrevPageLinkWithBorder,
	NextPageLink: NextPageLinkWithBorder,
};

export const ButtonsItemsGoTo: PaginatorTemplateOptions = {
	layout: layoutWithPageLinks,
	FirstPageLink: FirstPageLink,
	PrevPageLink: PrevPageLink,
	PageLinks: (options: any) => {
		const highlightClass = options.currentPage === options.page ? 'p-highlight' : '';

		return (
			<Button
				style={{ marginRight: '4px' }}
				className={`${highlightClass} p-page-btn`}
				theme="minimal"
				onClick={options.onClick}
			>
				{options.page + 1}
				<Ripple />
			</Button>
		);
	},
	NextPageLink: NextPageLink,
	LastPageLink: LastPageLink,
	RowsPerPageDropdown: RowsPerPageDropdownDropdownTemplate,
};

export const ButtonsItemsGoToBorder: PaginatorTemplateOptions = {
	layout: layoutWithPageLinks,
	FirstPageLink: FirstPageLinkWithBorder,
	PrevPageLink: (options: any) => {
		return (
			<Button
				aria-label="Prev"
				iconAfter="navigate_before"
				theme="tertiary"
				style={{ ...marginSize('0px', '4px', '0px', '4px') }}
				onClick={options.onClick}
				disabled={options.disabled}
			></Button>
		);
	},
	PageLinks: (options: any) => {
		const highlightClass = options.currentPage === options.page ? 'p-highlight' : '';

		return (
			<Button
				style={{ marginRight: '4px' }}
				className={`${highlightClass} p-page-btn`}
				theme="tertiary"
				onClick={options.onClick}
			>
				{options.page + 1}
				<Ripple />
			</Button>
		);
	},
	NextPageLink: (options: any) => {
		return (
			<Button
				aria-label="Next"
				iconAfter="navigate_next"
				theme="tertiary"
				style={{ marginRight: '4px' }}
				onClick={options.onClick}
				disabled={options.disabled}
			></Button>
		);
	},
	LastPageLink: LastPageLinkWithBorder,
	RowsPerPageDropdown: RowsPerPageDropdownDropdownTemplate,
};

export const Toolbar: PaginatorTemplateOptions = {
	layout: layoutToolbar,
	FirstPageLink: (options: any) => {
		return (
			<Button
				aria-label="First page"
				iconAfter="first_page"
				theme="tertiary"
				onClick={options.onClick}
				disabled={options.disabled}
				className="first-page-toolbar"
			></Button>
		);
	},
	PrevPageLink: (options: any) => {
		return (
			<Button
				aria-label="Prev"
				iconAfter="navigate_before"
				theme="tertiary"
				onClick={options.onClick}
				disabled={options.disabled}
				className="prev-page-toolbar"
			></Button>
		);
	},
	NextPageLink: (options: any) => {
		return (
			<Button
				aria-label="Next"
				iconAfter="navigate_next"
				theme="tertiary"
				onClick={options.onClick}
				disabled={options.disabled}
				className="next-page-toolbar"
			></Button>
		);
	},
	LastPageLink: (options: any) => {
		return (
			<Button
				aria-label="Last page"
				iconAfter="last_page"
				theme="tertiary"
				onClick={options.onClick}
				disabled={options.disabled}
				className="last-page-toolbar"
			></Button>
		);
	},
};

export const getPaginatorTemplate = (props: any) => {
	let paginatorStyle: any;

	switch (props.paginatorTemplate) {
		case 'Label':
			paginatorStyle = Label;
			break;

		case 'LabelBorder':
			paginatorStyle = LabelBorders;
			break;

		case 'GoTo':
			GoTo.CurrentPageReport = CurrentPageReportWithSimpleInput(props);
			paginatorStyle = GoTo;
			break;

		case 'GoToBorder':
			GoToBorder.CurrentPageReport = CurrentPageReportWithSimpleInput(props);
			paginatorStyle = GoToBorder;
			break;

		case 'GoToLabel':
			GoToLabel.CurrentPageReport = CurrentPageReportWithInputAndLabel(props);
			paginatorStyle = GoToLabel;
			break;

		case 'GoToLabelBorder':
			GoToLabelBorder.CurrentPageReport = CurrentPageReportWithInputAndLabel(props);
			paginatorStyle = GoToLabelBorder;
			break;

		case 'ButtonsItemsGoTo':
			ButtonsItemsGoTo.CurrentPageReport = CurrentPageReportWithInputAndGoToLabel(props);
			paginatorStyle = ButtonsItemsGoTo;
			break;

		case 'ButtonsItemsGoToBorder':
			ButtonsItemsGoToBorder.CurrentPageReport = CurrentPageReportWithInputAndGoToLabel(props);
			paginatorStyle = ButtonsItemsGoToBorder;
			break;

		case 'ItemsLabel':
			paginatorStyle = ItemsLabel;
			break;

		case 'ItemsLabelBorder':
			paginatorStyle = ItemsLabelBorder;
			break;

		case 'Toolbar':
			Toolbar.CurrentPageReport = CurrentPageReportWithSearch(props);
			paginatorStyle = Toolbar;
			break;

		default:
			paginatorStyle = {};
			break;
	}

	return paginatorStyle;
};
